import { Link, useRouteError } from "react-router-dom";
import { ApiError } from "../api/client.js";

/**
 * Friendly fallback for HTTP-shaped errors. Used both as a React Router
 * `errorElement` and as a stand-alone component when a query fails.
 */
export function ErrorPage({
  status,
  title,
  detail,
}: {
  status: number;
  title: string;
  detail?: string;
}) {
  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-4 text-center">
      <p className="text-sm font-mono text-slate-400">HTTP {status}</p>
      <h1 className="mt-1 text-xl font-semibold text-slate-900">{title}</h1>
      {detail && <p className="mt-2 text-sm text-slate-600">{detail}</p>}
      <Link
        to="/"
        className="mt-4 min-h-[44px] rounded-md bg-teal-700 px-4 py-2 text-sm font-medium text-white hover:bg-teal-800"
      >
        Back to home
      </Link>
    </div>
  );
}

/** Map an unknown error from a query/mutation to the right friendly page. */
export function ErrorPageFor(err: unknown) {
  if (err instanceof ApiError) {
    return <ErrorPage {...descriptionFor(err.status)} />;
  }
  return (
    <ErrorPage status={500} title="Something went wrong" detail="Please try again in a moment." />
  );
}

function descriptionFor(status: number): { status: number; title: string; detail?: string } {
  switch (status) {
    case 401:
      return {
        status: 401,
        title: "You need to sign in",
        detail: "Your session may have expired. Sign back in to continue.",
      };
    case 403:
      return {
        status: 403,
        title: "Access denied",
        detail: "You don’t have permission to view this page.",
      };
    case 404:
      return {
        status: 404,
        title: "Not found",
        detail: "We couldn’t find what you were looking for.",
      };
    default:
      return {
        status,
        title: status >= 500 ? "Server error" : "Something went wrong",
        detail:
          status >= 500
            ? "We hit a snag on our end. Please try again shortly."
            : "The request couldn’t be completed.",
      };
  }
}

/**
 * React Router error boundary. Used by route-level `errorElement` props.
 */
export function RouteErrorBoundary() {
  const err = useRouteError();
  if (err instanceof ApiError) {
    return <ErrorPage {...descriptionFor(err.status)} />;
  }
  return (
    <ErrorPage
      status={500}
      title="Something went wrong"
      detail={err instanceof Error ? err.message : undefined}
    />
  );
}
